<template>
  <div class="login-container">
    <el-card class="login-card">
      <div class="logo-container" @click="goToHomePage">
        <img :src="branding.logo" :alt="branding.logoAlt" class="logo">
        <div class="brand-text">
          <span class="brand-name">{{ branding.displayName }}</span>
          <span v-if="branding.subtitle" class="brand-sub">{{ branding.subtitle }}</span>
        </div>
      </div>
      
      <el-form :model="loginForm" :rules="rules" ref="loginForm">
        <el-form-item prop="student_email">
          <el-input v-model="loginForm.student_email" placeholder="User email"></el-input>
        </el-form-item>
        <div class="turnstile-container" v-if="!isDevMode">
          <div ref="turnstileWidget"></div>
        </div>
        <el-form-item prop="opt_code" style="width: 100%">
          <el-input v-model="loginForm.opt_code" auto-complete="off" placeholder="Please enter the code" type="text">
            <el-button slot="suffix" class="send_code" @click="sendCode" :disabled="countdown > 0 || (!isDevMode && !turnstileToken)">{{ countdown > 0 ? `${countdown}s` : 'Send Code' }}</el-button>
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="submitForm('loginForm')" style="width: 100%;">Login</el-button>
        </el-form-item>
      </el-form>
      <div class="signup-note">
        <span>Don't have an account? </span>
        <router-link :to="signupLink" class="signup-link">Sign Up</router-link>
        <div class="signup-extra">Sign up to access all features and save your progress.</div>
      </div>
    </el-card>
  </div>
</template>

<script>
import branding from '@/config/branding'

export default {
  name: 'Login',
  data() {
    return {
      branding,
      countdown: 0,
      turnstileToken: '',
      turnstileWidgetId: null,
      loginForm: {
        student_email: '',
        opt_code: ''
      },
      rules: {
        student_email: [
          { required: true, message: 'Please input username', trigger: 'blur' }
        ],
        opt_code: [
          { required: true, message: 'Please input opt code', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isDevMode() {
      return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    },
    signupLink() {
      // Pass redirect parameter to signup page if present
      const redirect = this.$route.query.redirect
      if (redirect) {
        return { path: '/signup', query: { redirect } }
      }
      return '/signup'
    }
  },
  mounted() {
    if (!this.isDevMode) {
      this.renderTurnstile()
    }
  },
  beforeDestroy() {
    if (this.turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId)
    }
  },
  methods: {
    getOnboardingCompletedKey(userId = this.$store.state.user_id) {
      return userId ? `onboardingCompleted:${userId}` : 'onboardingCompleted'
    },

    startCountdown() {
      this.countdown = 30;
      const timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(timer);
        }
      }, 1000);
    },

    goToHomePage() {
      this.$router.push('/about');
    },
    renderTurnstile() {
      if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      const tryRender = () => {
        if (window.turnstile && this.$refs.turnstileWidget) {
          this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileWidget, {
            sitekey: '0x4AAAAAACnvaf1-Ku2pDQ-n',
            theme: 'light',
            size: 'flexible',
            callback: (token) => { this.turnstileToken = token },
            'expired-callback': () => { this.turnstileToken = '' },
            'error-callback': () => { this.turnstileToken = '' }
          })
        } else {
          setTimeout(tryRender, 200)
        }
      }
      tryRender()
    },
    resetTurnstile() {
      this.turnstileToken = ''
      if (this.turnstileWidgetId !== null && window.turnstile) {
        window.turnstile.reset(this.turnstileWidgetId)
      }
    },
    sendCode() {
      if (this.loginForm.student_email == "") {
        this.$message.error("Please enter your email");
        return;
      }
      if (!this.isDevMode && !this.turnstileToken) {
        this.$message.error("Please complete the verification");
        return;
      }
      this.$store.dispatch('createLoginOPT', {
        student_email: this.loginForm.student_email,
        turnstile_token: this.turnstileToken || undefined
      }).then((res) => {
        console.log(res)
        this.resetTurnstile()
        if (res == null) {
          this.$message.success('OTP Sent');
          this.startCountdown();
        } else {
          this.$message.success(res);
          this.startCountdown();
        }
      })
    },

    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.$store.dispatch('loginByEmail', {
            student_email: this.loginForm.student_email,
            opt_code: this.loginForm.opt_code
          }).then((res) => {
            if (res == null) {
              this.$message.success('Login successful')

              // Check for redirect URL from query parameter
              const redirectUrl = this.$route.query.redirect

              // Check if user needs onboarding (no proper username set)
              const userId = this.$store.state.user_id
              const userName = (this.$store.state.user_info?.user_name || '').trim()
              const needsOnboarding = !userName || userName === 'Anonymous User'
              const onboardingCompleted = localStorage.getItem(this.getOnboardingCompletedKey(userId)) === 'true'

              if (needsOnboarding && !onboardingCompleted) {
                // Store redirect URL for after onboarding
                if (redirectUrl) {
                  localStorage.setItem('postLoginRedirect', redirectUrl)
                }
                this.$router.push({
                  path: '/onboarding',
                  query: redirectUrl ? { redirect: redirectUrl } : {}
                })
              } else if (redirectUrl) {
                if (!needsOnboarding) {
                  localStorage.setItem(this.getOnboardingCompletedKey(userId), 'true')
                }
                this.$router.push(redirectUrl)
              } else {
                if (!needsOnboarding) {
                  localStorage.setItem(this.getOnboardingCompletedKey(userId), 'true')
                }
                const accountRole = this.$store.state.user_info?.account_role
                this.$router.push(accountRole === 'creator' ? '/my-curation' : '/plaza')
              }
            } else {
              this.$message.success(res)
            }
          })
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
:deep(.el-input__suffix) {
  right: 0;
}

.send_code {}

.login-container {
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f2f5;
}

.login-card {
  width: 350px;
}

h2 {
  text-align: center;
  margin-bottom: 20px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  cursor: pointer;
}

.logo {
  height: 40px;
  margin-right: 10px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.brand-name {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0.5px;
}

.brand-sub {
  font-size: 14px;
  font-weight: 500;
  color: #909399;
  letter-spacing: 0.5px;
}

.turnstile-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.signup-note {
  text-align: center;
  margin-top: 18px;
  font-size: 14px;
  color: #666;
}
.signup-link {
  color: #1159AF;
  font-weight: 500;
  text-decoration: underline;
  margin-left: 3px;
}
.signup-link:hover {
  color: #409EFF;
}
.signup-extra {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

</style>
